// Real Firebase Feature Requests Service

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { db } from './firebase';

export interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: 'core' | 'ux' | 'integration' | 'mobile' | 'other';
  status: 'pending' | 'under-review' | 'planned' | 'in-progress' | 'completed' | 'declined';
  submittedBy: string;
  submittedByName?: string;
  voteCount: number;
  createdAt: string;
  updatedAt: string;
}

function docToFeatureRequest(docSnap: any): FeatureRequest {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    title: data.title,
    description: data.description,
    category: data.category,
    status: data.status,
    submittedBy: data.submittedBy,
    submittedByName: data.submittedByName || undefined,
    voteCount: data.voteCount || 0,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  };
}

export async function getAllFeatureRequests(): Promise<FeatureRequest[]> {
  const snapshot = await getDocs(collection(db, 'featureRequests'));
  const results = snapshot.docs.map(docToFeatureRequest);
  // If empty, seed with defaults
  if (results.length === 0) {
    await seedDefaultFeatureRequests();
    const seeded = await getDocs(collection(db, 'featureRequests'));
    return seeded.docs.map(docToFeatureRequest);
  }
  return results;
}

async function seedDefaultFeatureRequests(): Promise<void> {
  const defaults = [
    { title: 'Apple Music Integration', description: 'Add support for Apple Music alongside Spotify for song search and playback.', category: 'integration', status: 'planned', submittedBy: 'system', submittedByName: 'VoteBeats Team', voteCount: 47 },
    { title: 'Song Request Limits per Attendee', description: 'Allow DJs to set a maximum number of requests per attendee per event.', category: 'core', status: 'in-progress', submittedBy: 'system', submittedByName: 'VoteBeats Team', voteCount: 32 },
    { title: 'Custom Themes for Event Pages', description: 'Let DJs customize colors and branding for their event pages.', category: 'ux', status: 'pending', submittedBy: 'system', submittedByName: 'DJ Shadow', voteCount: 28 },
    { title: 'Offline Mode for Events', description: 'Allow the app to work offline and sync when connection is restored.', category: 'mobile', status: 'under-review', submittedBy: 'system', submittedByName: 'DJ Beats', voteCount: 19 },
  ];
  for (const item of defaults) {
    await addDoc(collection(db, 'featureRequests'), { ...item, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  }
}

export async function createFeatureRequest(data: {
  title: string;
  description: string;
  category: FeatureRequest['category'];
  submittedBy: string;
  submittedByName?: string;
}): Promise<FeatureRequest> {
  const docRef = await addDoc(collection(db, 'featureRequests'), {
    ...data,
    status: 'pending',
    voteCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const now = new Date().toISOString();
  return { id: docRef.id, ...data, status: 'pending', voteCount: 0, createdAt: now, updatedAt: now };
}

export async function getFeatureRequestById(id: string): Promise<FeatureRequest | null> {
  const docSnap = await getDoc(doc(db, 'featureRequests', id));
  if (!docSnap.exists()) return null;
  return docToFeatureRequest(docSnap);
}

export async function updateFeatureRequestStatus(id: string, status: FeatureRequest['status']): Promise<FeatureRequest | null> {
  const ref = doc(db, 'featureRequests', id);
  await updateDoc(ref, { status, updatedAt: serverTimestamp() });
  const updated = await getDoc(ref);
  if (!updated.exists()) return null;
  return docToFeatureRequest(updated);
}

export async function hasVotedForFeature(userId: string, featureId: string): Promise<boolean> {
  const q = query(
    collection(db, 'featureRequestVotes'),
    where('userId', '==', userId),
    where('featureId', '==', featureId)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

export async function voteForFeatureRequest(
  userId: string,
  featureId: string
): Promise<{ voteCount: number; hasVoted: boolean }> {
  const alreadyVoted = await hasVotedForFeature(userId, featureId);
  const featureRef = doc(db, 'featureRequests', featureId);

  if (alreadyVoted) {
    const q = query(
      collection(db, 'featureRequestVotes'),
      where('userId', '==', userId),
      where('featureId', '==', featureId)
    );
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    snapshot.docs.forEach(v => batch.delete(v.ref));
    batch.update(featureRef, { voteCount: increment(-1), updatedAt: serverTimestamp() });
    await batch.commit();
  } else {
    const batch = writeBatch(db);
    const voteRef = doc(collection(db, 'featureRequestVotes'));
    batch.set(voteRef, { userId, featureId, votedAt: serverTimestamp() });
    batch.update(featureRef, { voteCount: increment(1), updatedAt: serverTimestamp() });
    await batch.commit();
  }

  const updatedSnap = await getDoc(featureRef);
  const updatedCount = updatedSnap.data()?.voteCount || 0;
  return { voteCount: updatedCount, hasVoted: !alreadyVoted };
}

export function getFeatureRequestsStorageKey(): string {
  return 'featureRequests';
}

export function getCategoryLabel(category: FeatureRequest['category']): string {
  const labels: Record<FeatureRequest['category'], string> = {
    core: 'Core Features', ux: 'User Experience', integration: 'Integrations', mobile: 'Mobile', other: 'Other',
  };
  return labels[category];
}

export function getStatusLabel(status: FeatureRequest['status']): string {
  const labels: Record<FeatureRequest['status'], string> = {
    pending: 'Pending Review', 'under-review': 'Under Review', planned: 'Planned',
    'in-progress': 'In Progress', completed: 'Completed', declined: 'Declined',
  };
  return labels[status];
}
